import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiAlertCircle, FiInfo, FiCheckCircle, FiChevronUp, FiChevronDown, FiX } from 'react-icons/fi';

export interface StatusLogEntry {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  timestamp: Date;
  details?: string;
  affectedFiles?: string[];
}

interface ProjectStatusLogProps {
  entries: StatusLogEntry[];
  maxHeight?: string;
  onClearEntry?: (id: string) => void;
  onClearAll?: () => void;
}

export const ProjectStatusLog: React.FC<ProjectStatusLogProps> = ({
  entries,
  maxHeight = '200px',
  onClearEntry,
  onClearAll
}) => {
  const { darkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const logEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (isExpanded && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries, isExpanded]);

  const toggleEntryDetails = (id: string) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getIconForType = (type: StatusLogEntry['type']) => {
    switch (type) {
      case 'info':
        return <FiInfo className={darkMode ? 'text-blue-400' : 'text-blue-500'} />;
      case 'success':
        return <FiCheckCircle className={darkMode ? 'text-green-400' : 'text-green-500'} />;
      case 'warning':
        return <FiAlertCircle className={darkMode ? 'text-yellow-400' : 'text-yellow-500'} />;
      case 'error':
        return <FiAlertCircle className={darkMode ? 'text-red-400' : 'text-red-500'} />;
    }
  };

  return (
    <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Header */}
      <div 
        className={`px-3 py-2 flex justify-between items-center cursor-pointer ${
          darkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <span className="font-medium mr-2">Status Log</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            darkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
            {entries.length}
          </span>
        </div>
        <div className="flex items-center">
          {entries.length > 0 && onClearAll && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              className={`text-xs mr-2 px-2 py-1 rounded ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              Clear All
            </button>
          )}
          {isExpanded ? <FiChevronDown /> : <FiChevronUp />}
        </div>
      </div>

      {/* Log Content */}
      {isExpanded && (
        <div 
          className={`overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          style={{ maxHeight }}
        >
          {entries.length === 0 ? (
            <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No status updates yet
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map((entry) => (
                <div 
                  key={entry.id} 
                  className={`p-3 ${
                    darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="mt-0.5 mr-2">
                      {getIconForType(entry.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">{entry.message}</span>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {entry.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      
                      {/* Expandable details */}
                      {(entry.details || (entry.affectedFiles && entry.affectedFiles.length > 0)) && (
                        <div>
                          <button
                            onClick={() => toggleEntryDetails(entry.id)}
                            className={`text-xs mt-1 flex items-center ${
                              darkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}
                          >
                            {expandedEntries.has(entry.id) ? (
                              <>
                                <FiChevronDown className="mr-1" />
                                Hide details
                              </>
                            ) : (
                              <>
                                <FiChevronUp className="mr-1" />
                                Show details
                              </>
                            )}
                          </button>
                          
                          {expandedEntries.has(entry.id) && (
                            <div className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {entry.details && (
                                <div className="mb-2">{entry.details}</div>
                              )}
                              
                              {entry.affectedFiles && entry.affectedFiles.length > 0 && (
                                <div>
                                  <div className="font-medium mb-1">Affected files:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {entry.affectedFiles.map((file, i) => (
                                      <span 
                                        key={i} 
                                        className={`px-2 py-0.5 text-xs rounded-full ${
                                          darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                        }`}
                                      >
                                        {file}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Clear button */}
                    {onClearEntry && (
                      <button
                        onClick={() => onClearEntry(entry.id)}
                        className={`ml-2 p-1 rounded-full ${
                          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                        }`}
                        aria-label="Clear entry"
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
